import {
    Add as AddIcon,
    SmartToy as AgentIcon,
    School as CertificateIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Event as EventIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    PriorityHigh as HighPriorityIcon,
    MarkEmailRead as MarkReadIcon,
    Person as PersonIcon,
    Work as ProjectIcon,
    Psychology as PsychologyIcon,
    Reply as ReplyIcon,
    Computer as SystemIcon,
    Visibility as ViewIcon,
} from '@mui/icons-material';
import {
    Avatar,
    alpha,
    Box,
    Button,
    ButtonGroup,
    Checkbox,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { mockMessageService } from '../services/MockMessageService';
import { EMessageType, type IMessage } from '../types/IMessage';

type SortBy = 'date' | 'sender' | 'subject';
type FilterBy = 'all' | 'direct' | 'system' | 'notification' | 'agent' | 'unread';

export function InboxTab() {
    const theme = useTheme();
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState<IMessage | null>(null);
    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [sortBy, setSortBy] = useState<SortBy>('date');
    const [filterBy, setFilterBy] = useState<FilterBy>('all');
    const [expandedMessage, setExpandedMessage] = useState<string | null>(null);
    const [selectedMessages, setSelectedMessages] = useState<string[]>([]);
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);

    useEffect(() => {
        loadMessages();
    }, []);

    const loadMessages = async () => {
        try {
            const inboxMessages = await mockMessageService.getInboxMessages();
            setMessages(inboxMessages);
        } catch (error) {
            console.error('Error loading inbox messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMessageClick = async (message: IMessage) => {
        if (!message.read) {
            await mockMessageService.markAsRead(message.id);
            setMessages((prev) => prev.map((msg) => (msg.id === message.id ? { ...msg, read: true } : msg)));
        }
        setExpandedMessage(expandedMessage === message.id ? null : message.id);
    };

    const handleReply = (message: IMessage) => {
        setSelectedMessage(message);
        setReplyDialogOpen(true);
    };

    const handleSelectMessage = (messageId: string) => {
        setSelectedMessages((prev) =>
            prev.includes(messageId) ? prev.filter((id) => id !== messageId) : [...prev, messageId],
        );
    };

    const handleDeleteSelected = async () => {
        for (const messageId of selectedMessages) {
            await mockMessageService.deleteMessage(messageId);
        }
        setMessages((prev) => prev.filter((msg) => !selectedMessages.includes(msg.id)));
        setSelectedMessages([]);
    };

    const handleMarkSelectedAsRead = async () => {
        for (const messageId of selectedMessages) {
            await mockMessageService.markAsRead(messageId);
        }
        setMessages((prev) => prev.map((msg) => (selectedMessages.includes(msg.id) ? { ...msg, read: true } : msg)));
        setSelectedMessages([]);
    };

    const handleComposeNew = () => {
        // Navigate to compose tab or open compose dialog
        console.log('Navigate to compose');
    };

    const getMessageCTA = (message: IMessage) => {
        if (message.type === EMessageType.DIRECT) {
            return (
                <Button variant="outlined" size="small" startIcon={<ReplyIcon />} onClick={() => handleReply(message)}>
                    {langStrings.messages.reply}
                </Button>
            );
        }

        if (message.type === EMessageType.NOTIFICATION) {
            if (message.subject.toLowerCase().includes('skill')) {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => console.log('Skill-Vorschläge anzeigen')}
                    >
                        {langStrings.messages.showSuggestions}
                    </Button>
                );
            }
            if (message.subject.toLowerCase().includes('zertifikat')) {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<CertificateIcon />}
                        onClick={() => console.log('Zertifikat verwalten')}
                    >
                        {langStrings.messages.manageCertificate}
                    </Button>
                );
            }
            if (message.subject.toLowerCase().includes('projekt')) {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<ProjectIcon />}
                        onClick={() => console.log('Projekt anzeigen')}
                    >
                        {langStrings.messages.showProject}
                    </Button>
                );
            }
        }

        if (message.type === EMessageType.AGENT) {
            if (message.subject.toLowerCase().includes('code') || message.subject.toLowerCase().includes('review')) {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => console.log('Code-Review anzeigen')}
                    >
                        {langStrings.messages.showResults}
                    </Button>
                );
            }
            if (message.subject.toLowerCase().includes('dokumentation')) {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => console.log('Dokumentation anzeigen')}
                    >
                        {langStrings.messages.openDocumentation}
                    </Button>
                );
            }
        }

        if (message.type === EMessageType.SYSTEM) {
            if (message.subject.toLowerCase().includes('profil')) {
                return (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => console.log('Profil bearbeiten')}
                    >
                        {langStrings.messages.editProfile}
                    </Button>
                );
            }
            if (message.subject.toLowerCase().includes('termin') || message.subject.toLowerCase().includes('meeting')) {
                return (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EventIcon />}
                        onClick={() => console.log('Termin anzeigen')}
                    >
                        {langStrings.messages.showAppointment}
                    </Button>
                );
            }
        }

        return (
            <Button
                variant="outlined"
                size="small"
                startIcon={<ViewIcon />}
                onClick={() => console.log('Details anzeigen')}
            >
                {langStrings.messages.showDetails}
            </Button>
        );
    };

    const getMessageIcon = (type: EMessageType) => {
        switch (type) {
            case EMessageType.DIRECT:
                return <PersonIcon sx={{ color: theme.palette.primary.main }} />;
            case EMessageType.SYSTEM:
                return <SystemIcon sx={{ color: theme.palette.info.main }} />;
            case EMessageType.NOTIFICATION:
                return <PsychologyIcon sx={{ color: '#4dcea9' }} />;
            case EMessageType.AGENT:
                return (
                    <Avatar
                        sx={{
                            width: 28,
                            height: 28,
                            bgcolor: alpha(theme.palette.primary.light, 0.15),
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: 1,
                        }}
                    >
                        <AgentIcon
                            sx={{
                                fontSize: 18,
                                color: theme.palette.primary.light,
                            }}
                        />
                    </Avatar>
                );
            default:
                return <PersonIcon />;
        }
    };

    const formatTimestamp = (timestamp: Date) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (days > 0) {
            return `${days} ${langStrings.messages.daysAgo}`;
        } else if (hours > 0) {
            return `${hours} ${langStrings.messages.hoursAgo}`;
        } else {
            return langStrings.messages.justNow;
        }
    };

    const formatFullDate = (timestamp: Date) => {
        return new Date(timestamp).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const isToday = (timestamp: Date) => {
        const today = new Date();
        const messageDate = new Date(timestamp);
        return today.toDateString() === messageDate.toDateString();
    };

    const getSortedAndFilteredMessages = () => {
        let filtered = messages;

        // Filter
        switch (filterBy) {
            case 'direct':
                filtered = messages.filter((msg) => msg.type === EMessageType.DIRECT);
                break;
            case 'system':
                filtered = messages.filter((msg) => msg.type === EMessageType.SYSTEM);
                break;
            case 'notification':
                filtered = messages.filter((msg) => msg.type === EMessageType.NOTIFICATION);
                break;
            case 'agent':
                filtered = messages.filter((msg) => msg.type === EMessageType.AGENT);
                break;
            case 'unread':
                filtered = messages.filter((msg) => !msg.read);
                break;
            default:
                filtered = messages;
        }

        // Sort
        return filtered.sort((a, b) => {
            switch (sortBy) {
                case 'date':
                    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
                case 'sender': {
                    const senderA = typeof a.from === 'string' ? 'System' : `${a.from.firstName} ${a.from.lastName}`;
                    const senderB = typeof b.from === 'string' ? 'System' : `${b.from.firstName} ${b.from.lastName}`;
                    return senderA.localeCompare(senderB);
                }
                case 'subject':
                    return a.subject.localeCompare(b.subject);
                default:
                    return 0;
            }
        });
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>{langStrings.messages.loadingMessages}</Typography>
            </Box>
        );
    }

    const sortedFilteredMessages = getSortedAndFilteredMessages();

    if (sortedFilteredMessages.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">{langStrings.messages.noMessages}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            {/* Control Bar */}
            <Box
                sx={{
                    mb: { xs: 2, sm: 3 },
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.5, sm: 2 }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                >
                    {/* Action Buttons Group */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 2 },
                            alignItems: { xs: 'stretch', sm: 'center' },
                        }}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleComposeNew}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                py: { xs: 1, sm: 0.5 },
                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                            }}
                        >
                            {langStrings.messages.newMessage}
                        </Button>

                        <Box
                            sx={{
                                display: { xs: 'flex', sm: 'flex' },
                                gap: 1,
                                '& .MuiButton-root': {
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 400,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    px: { xs: 1.5, sm: 2 },
                                    py: { xs: 0.75, sm: 0.5 },
                                    minWidth: { xs: 'auto', sm: 64 },
                                    flex: { xs: 1, sm: 'none' },
                                },
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<MarkReadIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                                disabled={selectedMessages.length === 0}
                                onClick={handleMarkSelectedAsRead}
                            >
                                {selectedMessages.length > 0
                                    ? `${selectedMessages.length} ${langStrings.messages.markAsRead}`
                                    : langStrings.messages.markAsRead}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DeleteIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                                disabled={selectedMessages.length === 0}
                                onClick={handleDeleteSelected}
                                color="error"
                            >
                                {selectedMessages.length > 0
                                    ? `${selectedMessages.length} ${langStrings.messages.delete}`
                                    : langStrings.messages.delete}
                            </Button>
                        </Box>
                    </Box>

                    {/* Filter Controls */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: { xs: 1, sm: 1.5 },
                            alignItems: 'center',
                            flexDirection: { xs: 'row', sm: 'row' },
                            '& .MuiFormControl-root': {
                                flex: { xs: 1, sm: 'none' },
                            },
                        }}
                    >
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: { xs: 'auto', sm: 110 },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backdropFilter: 'blur(8px)',
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                },
                            }}
                        >
                            <InputLabel>{langStrings.messages.filter}</InputLabel>
                            <Select
                                value={filterBy}
                                label="Filter"
                                onChange={(e) => setFilterBy(e.target.value as FilterBy)}
                            >
                                <MenuItem value="all">{langStrings.messages.all}</MenuItem>
                                <MenuItem value="unread">{langStrings.messages.unread}</MenuItem>
                                <MenuItem value="direct">{langStrings.messages.direct}</MenuItem>
                                <MenuItem value="system">{langStrings.messages.system}</MenuItem>
                                <MenuItem value="notification">{langStrings.messages.notifications}</MenuItem>
                                <MenuItem value="agent">{langStrings.messages.agent}</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: { xs: 'auto', sm: 110 },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backdropFilter: 'blur(8px)',
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                },
                            }}
                        >
                            <InputLabel>{langStrings.messages.sort}</InputLabel>
                            <Select
                                value={sortBy}
                                label="Sortieren"
                                onChange={(e) => setSortBy(e.target.value as SortBy)}
                            >
                                <MenuItem value="date">{langStrings.messages.date}</MenuItem>
                                <MenuItem value="sender">{langStrings.messages.sender}</MenuItem>
                                <MenuItem value="subject">{langStrings.messages.subject}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Stack>
            </Box>

            {/* Messages List */}
            <Paper elevation={1}>
                <List sx={{ p: 0 }}>
                    {sortedFilteredMessages.map((message, index) => {
                        const isExpanded = expandedMessage === message.id;
                        const isSelected = selectedMessages.includes(message.id);
                        const isMobile = theme.breakpoints.down('sm');

                        return (
                            <React.Fragment key={message.id}>
                                <ListItem
                                    sx={{
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        cursor: 'pointer',
                                        py: { xs: 1.5, sm: 2 },
                                        px: { xs: 1, sm: 2 },
                                        bgcolor: isSelected
                                            ? alpha(theme.palette.primary.main, 0.3)
                                            : message.read
                                              ? 'transparent'
                                              : alpha(theme.palette.primary.main, 0.2),
                                        '&:hover': {
                                            bgcolor: isSelected
                                                ? alpha(theme.palette.primary.main, 0.4)
                                                : message.read
                                                  ? alpha(theme.palette.primary.main, 0.05)
                                                  : alpha(theme.palette.primary.main, 0.4),
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            width: '100%',
                                            gap: { xs: 1, sm: 2 },
                                        }}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => handleSelectMessage(message.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            size="small"
                                            sx={{
                                                mt: 0.5,
                                                display: { xs: 'none', sm: 'flex' },
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexShrink: 0,
                                                mt: 0.5,
                                            }}
                                        >
                                            {getMessageIcon(message.type)}
                                        </Box>

                                        <Box
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                overflow: 'hidden',
                                            }}
                                            onClick={() => handleMessageClick(message)}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                                    justifyContent: 'space-between',
                                                    mb: 0.5,
                                                    gap: { xs: 0.5, sm: 2 },
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: message.read ? 400 : 600,
                                                        color: message.read ? 'text.primary' : 'primary.main',
                                                        fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                        lineHeight: 1.2,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: { xs: 2, sm: 1 },
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                >
                                                    {message.subject}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        flexShrink: 0,
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {formatTimestamp(message.timestamp)}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                                    justifyContent: 'space-between',
                                                    gap: { xs: 0.5, sm: 1 },
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontWeight: message.read ? 400 : 500,
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        maxWidth: { xs: '100%', sm: '60%' },
                                                    }}
                                                >
                                                    {typeof message.from === 'string'
                                                        ? message.type === EMessageType.AGENT
                                                            ? 'T4LO Agent'
                                                            : 'System'
                                                        : `${message.from.firstName} ${message.from.lastName}`}
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        display: { xs: 'none', sm: 'flex' },
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    {!isToday(message.timestamp) && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ fontSize: '0.7rem' }}
                                                        >
                                                            {formatFullDate(message.timestamp)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                flexShrink: 0,
                                                mt: 0.5,
                                            }}
                                        >
                                            {message.priority === 'high' && (
                                                <HighPriorityIcon color="error" sx={{ fontSize: { xs: 16, sm: 20 } }} />
                                            )}
                                            {!message.read && (
                                                <Box
                                                    sx={{
                                                        width: { xs: 6, sm: 8 },
                                                        height: { xs: 6, sm: 8 },
                                                        borderRadius: '50%',
                                                        bgcolor: 'primary.main',
                                                    }}
                                                />
                                            )}
                                            {isExpanded ? (
                                                <ExpandLessIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                            ) : (
                                                <ExpandMoreIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                            )}
                                        </Box>
                                    </Box>

                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Box
                                            sx={{
                                                pl: { xs: 1, sm: 6 },
                                                pr: { xs: 1, sm: 2 },
                                                pb: 2,
                                                pt: 1,
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Divider sx={{ mb: 2 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    mb: 2,
                                                    lineHeight: 1.5,
                                                    color: 'text.primary',
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word',
                                                }}
                                            >
                                                {message.content}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: { xs: 'center', sm: 'flex-start' },
                                                    '& .MuiButton-root': {
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        minWidth: { xs: 'auto', sm: 64 },
                                                        px: { xs: 2, sm: 3 },
                                                    },
                                                }}
                                            >
                                                {getMessageCTA(message)}
                                            </Box>
                                        </Box>
                                    </Collapse>
                                </ListItem>
                                {index < sortedFilteredMessages.length - 1 && <Divider />}
                            </React.Fragment>
                        );
                    })}
                </List>
            </Paper>

            {/* Reply Dialog */}
            <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{langStrings.messages.reply}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {langStrings.messages.replyTo} {selectedMessage?.subject}
                    </Typography>
                    <Typography variant="body2">{langStrings.messages.replyFunctionNotImplemented}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReplyDialogOpen(false)}>{langStrings.messages.close}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
